import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { makeKey, getCache, setCache } from "@/lib/offlineCache";
import OfflineBadge from "@/components/OfflineBadge";
import DatePicker from "@/components/DatePicker";
import ShareCard from "@/components/ShareCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hash, Sparkles, Car, Home, Phone, Palette, Gem, Calendar, Grid3X3, Brain } from "lucide-react";

export default function Numerology() {
  const guard = usePaywallGuard();
  const [form, setForm] = useState({ dob: "", name: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cachedAt, setCachedAt] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setReport(null);
    setCachedAt(null);
    const cacheKey = makeKey("numerology-lucky", form);
    const cached = getCache(cacheKey);
    if (cached) { setReport(cached.data); setCachedAt(cached.cachedAt); }
    try {
      const data = await guard(
        async () => (await api.post("/numerology/lucky", form)).data,
        "Lucky Numbers Report"
      );
      setReport(data);
      setCachedAt(null);
      setCache(cacheKey, data);
    } catch (err) {
      if (!cached) setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="numerology-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Lucky Numbers Report</h1>
        <p className="text-slate-400">Discover your driver, conductor, and lucky combinations based on your birth date.</p>
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="numerology-form">
          <div>
            <Label className="text-slate-300">Date of Birth</Label>
            <div className="mt-1.5">
              <DatePicker
                value={form.dob}
                onChange={(v) => setForm({ ...form, dob: v })}
                testId="numerology-dob-input"
                placeholder="Pick your date of birth"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Full Name (optional)</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="numerology-name-input"
              className="mt-1.5 bg-slate-900/50 border-white/10 text-white"
              placeholder="For name + DOB report"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={loading}
              data-testid="numerology-submit-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium"
            >
              {loading ? "Calculating..." : "Reveal Lucky Numbers"}
            </Button>
          </div>
        </form>
        {error && <div className="text-sm text-rose-400 mt-3" data-testid="numerology-error">{error}</div>}
      </Card>

      {report && (
        <div className="space-y-6 animate-fade-up" data-testid="numerology-report">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <ShareCard report={report} name={form.name} dob={form.dob} />
            {cachedAt && <OfflineBadge cachedAt={cachedAt} testId="numerology-offline-badge" />}
          </div>
          {/* Premium upsell row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PremiumCTA
              to="/lo-shu-grid"
              icon={Grid3X3}
              title="See your Lo Shu Grid"
              subtitle="3×3 Saturn box with missing-number remedies"
              testId="cta-lo-shu"
            />
            <DeepAnalysisCTA dob={form.dob} name={form.name} />
          </div>

          {/* Driver & Conductor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberCard label={report.driver.label} number={report.driver.number} meaning={report.driver.meaning} />
            <NumberCard label={report.conductor.label} number={report.conductor.number} meaning={report.conductor.meaning} />
          </div>

          {/* Lucky details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <LuckyCard icon={Sparkles} title="Lucky Numbers" items={report.lucky_numbers} testId="lucky-numbers" />
            <LuckyCard icon={Home} title="Lucky House Numbers" items={report.lucky_house_numbers} testId="lucky-house" />
            <LuckyCard icon={Car} title="Lucky Car No. (digit sums)" items={report.lucky_car_number_sums} testId="lucky-car"
                       hint="Total of all digits on plate should equal one of these" />
            <LuckyCard icon={Phone} title="Lucky Mobile Sum" items={report.lucky_mobile_sum} testId="lucky-mobile"
                       hint="Sum of digits of your mobile number" />
            <LuckyCard icon={Palette} title="Lucky Colors" items={report.lucky_colors} testId="lucky-colors" />
            <LuckyCard icon={Calendar} title="Lucky Days" items={report.lucky_days} testId="lucky-days" />
            <LuckyCard icon={Gem} title="Lucky Gemstones" items={report.lucky_gemstones} testId="lucky-gems" />
            <LuckyCard icon={Hash} title="Compatible Numbers" items={report.compatible_numbers} testId="compatible-numbers" />
          </div>

          {report.name_numerology && (
            <Card className="glass border-white/10 p-6" data-testid="name-num-section">
              <h3 className="font-serif text-2xl text-slate-50 mb-4">Name Numerology — {report.name_numerology.name}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <NameAspect label="Destiny" data={report.name_numerology.destiny} />
                <NameAspect label="Soul Urge" data={report.name_numerology.soul_urge} />
                <NameAspect label="Personality" data={report.name_numerology.personality} />
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function NumberCard({ label, number, meaning }) {
  return (
    <Card className="glass border-white/10 p-6 card-hover">
      <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">{label}</div>
      <div className="flex items-baseline gap-4 mb-4">
        <div className="font-serif text-7xl text-gold leading-none">{number}</div>
        <div>
          <div className="text-lg text-slate-50 font-medium">{meaning.title}</div>
          <div className="text-xs text-slate-400">Planet: {meaning.planet}</div>
        </div>
      </div>
      <p className="text-sm text-slate-300 leading-relaxed">{meaning.traits}</p>
    </Card>
  );
}

function LuckyCard({ icon: Icon, title, items, hint, testId }) {
  return (
    <Card className="glass border-white/10 p-5 card-hover" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gold" />
        <h4 className="text-sm font-medium text-slate-200 uppercase tracking-wider">{title}</h4>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <span
            key={i}
            className="px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm font-medium"
          >
            {it}
          </span>
        ))}
      </div>
      {hint && <p className="text-xs text-slate-500 mt-3">{hint}</p>}
    </Card>
  );
}

function PremiumCTA({ to, icon: Icon, title, subtitle, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.07] to-rose-500/[0.05] p-5 flex items-center gap-4 transition hover:border-amber-500/50 hover:shadow-[0_0_30px_-12px_rgba(245,195,110,0.45)]"
    >
      <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-50">{title}</div>
        <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>
      </div>
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold uppercase tracking-wider">Premium</span>
    </Link>
  );
}

function DeepAnalysisCTA({ dob, name }) {
  const guard = usePaywallGuard();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deep, setDeep] = useState(null);
  const [error, setError] = useState("");

  const fetchDeep = async () => {
    if (deep || loading) { setOpen(!open); return; }
    setLoading(true);
    setError("");
    try {
      const data = await guard(
        async () => (await api.post("/numerology/deep-analysis", { dob, name })).data,
        "Detailed Driver/Conductor Analysis"
      );
      setDeep(data);
      setOpen(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="cta-deep-analysis-wrap">
      <button
        type="button"
        onClick={fetchDeep}
        disabled={loading}
        data-testid="cta-deep-analysis"
        className="w-full group relative rounded-2xl border border-rose-500/25 bg-gradient-to-br from-rose-500/[0.07] to-amber-500/[0.05] p-5 flex items-center gap-4 transition hover:border-rose-500/50 hover:shadow-[0_0_30px_-12px_rgba(251,113,133,0.45)] text-left disabled:opacity-60"
      >
        <div className="w-11 h-11 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
          <Brain className="w-5 h-5 text-rose-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-50">
            {loading ? "Generating deep analysis…" : "Detailed Driver + Conductor analysis"}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            Synergy, career fit, partner numbers, life themes
          </div>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 font-semibold uppercase tracking-wider">Premium</span>
      </button>
      {error && <div className="text-xs text-rose-400 mt-2">{error}</div>}
      {open && deep && (
        <Card className="glass border-white/10 p-5 mt-3 animate-fade-up" data-testid="deep-analysis-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-300/80">Driver {deep.driver.number} · {deep.driver.planet}</div>
              <div className="text-sm text-slate-200 mt-1">{deep.driver.theme}</div>
            </div>
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-rose-300/80">Conductor {deep.conductor.number} · {deep.conductor.planet}</div>
              <div className="text-sm text-slate-200 mt-1">{deep.conductor.theme}</div>
            </div>
          </div>
          <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3 mb-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Synergy · {deep.synergy.verdict}</div>
            <div className="text-sm text-slate-300 leading-relaxed">{deep.synergy.note}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Career fit</div>
              <div className="flex flex-wrap gap-1.5">
                {deep.career_suggestions.map((c) => (
                  <span key={c} className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300">{c}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Compatible partner numbers</div>
              <div className="flex flex-wrap gap-1.5">
                {deep.partner_numbers.map((n) => (
                  <span key={n} className="text-[11px] px-2.5 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300">{n}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            {deep.forecast.this_year} {deep.forecast.next_3_years}
          </div>
        </Card>
      )}
    </div>
  );
}

function NameAspect({ label, data }) {
  return (
    <div className="p-4 rounded-xl bg-slate-900/40 border border-white/5">
      <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">{label}</div>
      <div className="text-3xl font-serif text-slate-50">{data.number}</div>
      <div className="text-sm text-slate-300 mt-1">{data.meaning.title}</div>
      <div className="text-xs text-slate-400 mt-1">{data.meaning.traits}</div>
    </div>
  );
}
